import React from "react";
import { Draggable, DraggableProvided } from "react-beautiful-dnd";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Collapse, Stack, Typography } from "@mui/material";
import { DealStage, DealStageGroup, DealStageItem, isDealStageGroup } from "store/types";

import { INotePopup } from "..";
import { encodeGroupPath, groupKeyPath } from "../Pipeline/pipeline-utils";

interface Props {
  stage: DealStage;
  isCollapsed: (key: string) => boolean;
  onToggleGroup: (key: string) => void;
  dragDisabled?: boolean;
  setNotePopup?: (data: INotePopup) => void;
  renderCard: (
    item: DealStageItem,
    draggableProvided: DraggableProvided | undefined,
    isDragging: boolean,
    stageId: number,
    setNotePopup?: (data: INotePopup) => void
  ) => React.ReactNode;
}

interface GroupHeaderProps {
  label: string;
  count: number;
  collapsed: boolean;
  depth: number;
  onToggle: () => void;
}

const GroupHeader: React.FC<GroupHeaderProps> = ({ label, count, collapsed, depth, onToggle }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap="4px"
    onClick={onToggle}
    sx={{ pl: `${depth * 12}px`, py: "4px", cursor: "pointer", userSelect: "none", ":hover": { opacity: 0.85 } }}
  >
    {collapsed ? (
      <ChevronRightIcon sx={{ fontSize: "16px", color: "#071f35" }} />
    ) : (
      <ExpandMoreIcon sx={{ fontSize: "16px", color: "#071f35" }} />
    )}
    <Typography sx={{ fontSize: "14px", fontWeight: 500, lineHeight: "16px", color: "#071f35" }}>{label}</Typography>
    <Typography sx={{ fontSize: "15px", color: "#92a7bc", ml: "auto" }}>{count}</Typography>
  </Stack>
);

/**
 * Renders a stage's grouped tree inside the column's single Droppable. Cards are draggable with column-global,
 * contiguous indices (react-beautiful-dnd requirement); the drop target is the whole column and the drop handler
 * checks whether the destination stage has the dragged card's group.
 */
export const GroupedColumnContent: React.FC<Props> = ({
  stage,
  isCollapsed,
  onToggleGroup,
  setNotePopup,
  renderCard,
  dragDisabled
}) => {
  // Assign a global index to every currently-visible card (collapsed groups are unmounted, so excluded).
  const indexByCard = new Map<string, number>();
  let visibleCounter = 0;
  const indexVisible = (nodes: Array<DealStageGroup | DealStageItem>, parentPath: string[]) => {
    nodes.forEach((node) => {
      if (!isDealStageGroup(node)) return;
      const path = [...parentPath, node.group_title];
      if (isCollapsed(groupKeyPath(stage.id, path))) return;
      if (node.children.length > 0 && !isDealStageGroup(node.children[0])) {
        (node.children as DealStageItem[]).forEach((deal) => {
          indexByCard.set(`${encodeGroupPath(path)}:${deal.id}`, visibleCounter);
          visibleCounter += 1;
        });
      } else {
        indexVisible(node.children, path);
      }
    });
  };
  indexVisible(stage.groups ?? [], []);

  const renderNodes = (nodes: Array<DealStageGroup | DealStageItem>, parentPath: string[]): React.ReactNode =>
    nodes.map((node) => {
      if (!isDealStageGroup(node)) return null;

      const path = [...parentPath, node.group_title];
      const encodedPath = encodeGroupPath(path);
      const key = groupKeyPath(stage.id, path);
      const collapsed = isCollapsed(key);
      const isLeafGroup = node.children.length > 0 && !isDealStageGroup(node.children[0]);

      return (
        <Stack key={key} sx={{ gap: "8px" }}>
          <GroupHeader
            label={node.group_title}
            count={node.deals_count}
            collapsed={collapsed}
            depth={path.length - 1}
            onToggle={() => onToggleGroup(key)}
          />
          <Collapse
            in={!collapsed}
            timeout={220}
            unmountOnExit
            sx={{ "& .MuiCollapse-wrapperInner": { display: "flex", flexDirection: "column", gap: "8px" } }}
          >
            {isLeafGroup
              ? (node.children as DealStageItem[]).map((deal) => (
                  <Draggable
                    key={`${encodedPath}:${deal.id}`}
                    draggableId={`deal:${stage.id}:${deal.id}:${encodedPath}`}
                    index={indexByCard.get(`${encodedPath}:${deal.id}`) ?? 0}
                    isDragDisabled={dragDisabled}
                  >
                    {(provided, snapshot) => (
                      <>{renderCard(deal, provided, snapshot.isDragging, stage.id, setNotePopup)}</>
                    )}
                  </Draggable>
                ))
              : renderNodes(node.children, path)}
          </Collapse>
        </Stack>
      );
    });

  return <>{renderNodes(stage.groups ?? [], [])}</>;
};
